import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { EmailEditor } from '@/components/ui/email-editor';
import { GmailPreviewModal } from '@/components/ui/gmail-preview-modal';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { SequenceStep } from '@/lib/services/campaigns';
import { replaceVariables, ContactData } from '@/lib/utils/replace-variables';

interface ValidationError {
  stepId: string;
  stepIndex: number;
  field: 'subject' | 'content' | 'senderEmail';
  message: string;
}

interface Lead {
  id: number | string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  linkedinUrl: string | null;
}

interface StepEditorPanelProps {
  selectedStep: SequenceStep | undefined;
  emailAccounts: any[];
  onUpdateStep: (updates: Partial<SequenceStep>) => void;
  isReadOnly?: boolean;
  validationErrors?: ValidationError[];
  leads?: Lead[];
  /** The campaign's sender pool, shared by every step. */
  senderEmails?: string[];
  /** Persist a changed pool. Omitted = read only. */
  onChangeSenderEmails?: (next: string[]) => void | Promise<void>;
}

export function StepEditorPanel({
  selectedStep,
  emailAccounts,
  onUpdateStep,
  isReadOnly = false,
  validationErrors = [],
  leads = [],
  senderEmails = [],
  onChangeSenderEmails,
}: StepEditorPanelProps) {
  const [showPreview, setShowPreview] = useState(false);

  // Connected mailboxes, keyed for the pool picker. The pool is one list for
  // the whole campaign: picking here changes it for every step, and each
  // contact keeps whichever member first wrote to them.
  const readyAccounts = emailAccounts.filter((acc: any) => !acc.status || acc.status === 'ready' || acc.status === 'active');
  const selectedKeys = new Set(senderEmails.map(e => e.toLowerCase()));
  const poolEditable = !isReadOnly && Boolean(onChangeSenderEmails);
  const togglePoolMember = (email: string) => {
    if (!poolEditable || !onChangeSenderEmails) return;
    const key = email.toLowerCase();
    const next = selectedKeys.has(key)
      ? senderEmails.filter(e => e.toLowerCase() !== key)
      : [...senderEmails, email];
    void onChangeSenderEmails(next);
  };
  const poolLabel = senderEmails.length === 0
    ? 'Select sending mailboxes'
    : senderEmails.length === 1
      ? senderEmails[0]
      : `${senderEmails[0]} +${senderEmails.length - 1} more`;
  const poolLabelWidth = `${'Sender for emails'.length * 8 + 48}px`;

  const stepErrors = validationErrors.filter(e => e.stepId === selectedStep?.id);
  const hasSubjectError = stepErrors.some(e => e.field === 'subject');
  const hasContentError = stepErrors.some(e => e.field === 'content');

  if (!selectedStep) {
    return null;
  }

  // Get the first lead for preview, or use defaults
  const previewContact: ContactData | undefined = leads.length > 0 ? {
    firstName: leads[0].firstName || undefined,
    lastName: leads[0].lastName || undefined,
    email: leads[0].email || undefined,
    company: leads[0].company || undefined,
    linkedinUrl: leads[0].linkedinUrl || undefined,
  } : undefined;

  // The preview shows the pool's first mailbox. Which member a given lead
  // actually gets is decided by the mailer at the lead's first send, and every
  // later step to that lead comes from the same mailbox.
  const previewSender = senderEmails[0] || selectedStep.senderEmail || '';
  const senderAccount = emailAccounts.find(acc => acc.email === previewSender);
  const senderName = senderAccount?.display_name || '';
  const senderEmail = previewSender || 'sender@example.com';

  // Replace variables in subject and content for preview
  const previewSubject = replaceVariables(selectedStep.subject || '', previewContact);
  const previewBody = replaceVariables(selectedStep.content || '', previewContact);
  const recipientEmail = previewContact?.email || 'john.doe@example.com';
  const recipientName = previewContact?.firstName
    ? `${previewContact.firstName}${previewContact.lastName ? ' ' + previewContact.lastName : ''}`
    : undefined;

  return (
    <div 
      className="flex-[2] max-w-[50%] ml-auto pl-8 pr-2 pt-3 overflow-visible flex flex-col relative z-10 border-l border-line-strong/60"
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center gap-1 items-center justify-start mb-8 ml-2">
            <EnvelopeIcon className="h-7 w-7 text-foreground" />
          <div className="flex flex-col items-start gap-1 ml-2">
            <h3 className="text-md font-medium mb-0">Email</h3>
            <p className="text-xs mt-0">Send Automatic email</p>
          </div>
        </div>
        
        <div className="flex flex-col flex-1 min-h-0 gap-0">
          <div className="mb-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative max-w-lg">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none text-sm font-normal z-10">
                    Sender for emails
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild disabled={!poolEditable}>
                      <button
                        type="button"
                        className={`flex h-12 w-full items-center justify-between gap-2 rounded-full border bg-input-bg pr-3 py-2 text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                          senderEmails.length === 0 ? 'border-ring-error' : 'border-control-border'
                        }`}
                        style={{ paddingLeft: poolLabelWidth }}
                      >
                        <span className={`flex-1 min-w-0 truncate text-left ${senderEmails.length === 0 ? 'text-muted-foreground' : ''}`}>
                          {poolLabel}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 opacity-50 shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[28rem]">
                      <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">
                        Tick every mailbox this campaign should send from. New contacts are spread across them; each contact keeps the mailbox that first wrote to them.
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {readyAccounts.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground">No connected mailboxes yet. Add one under Sender Accounts.</div>
                      ) : (
                        readyAccounts.map((account: any) => {
                          const throttled = account.warmup_throttle ?? true;
                          const ramping = Boolean(throttled && account.warmup?.ramping);
                          const cap = ramping && account.warmup?.dailyCap != null ? account.warmup.dailyCap : 25;
                          return (
                            <DropdownMenuCheckboxItem
                              key={account.account_id || account.email}
                              checked={selectedKeys.has(account.email.toLowerCase())}
                              onCheckedChange={() => togglePoolMember(account.email)}
                              onSelect={(e) => e.preventDefault()}
                            >
                              <span className="flex w-full items-center justify-between gap-4">
                                <span className="truncate">{account.email}</span>
                                <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                                  {ramping && account.warmup ? `day ${account.warmup.sendingDay} · ` : ''}{cap}/day
                                </span>
                              </span>
                            </DropdownMenuCheckboxItem>
                          );
                        })
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TooltipTrigger>
              {isReadOnly && (
                <TooltipContent>
                  <p>Can't edit while campaign is live</p>
                </TooltipContent>
              )}
            </Tooltip>
            {senderEmails.length > 1 && (
              <p className="text-xs text-muted-foreground mt-2 ml-2">
                Rotates across {senderEmails.length} mailboxes. Preview shows {senderEmails[0]}.
              </p>
            )}
          </div>

          <div className="mt-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Input
                    id="email-title"
                    value={selectedStep?.subject || ''}
                    onChange={isReadOnly ? undefined : (e) => onUpdateStep({ subject: e.target.value })}
                    placeholder="required"
                    inlineLabel="Subject"
                    className="w-full mb-3"
                    style={{ paddingLeft: '12.27rem' }}
                    disabled={isReadOnly}
                    hasError={hasSubjectError}
                  />
                </div>
              </TooltipTrigger>
              {isReadOnly && (
                <TooltipContent>
                  <p>Can't edit while campaign is live</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex-1 min-h-0 flex flex-col relative z-10">
                <EmailEditor
                  value={selectedStep?.content || ''}
                  onChange={isReadOnly ? () => {} : (value) => {
                    onUpdateStep({ content: value });
                  }}
                  placeholder="Write your email... Use {AI instructions} for personalised sections"
                  readOnly={isReadOnly}
                  hasError={hasContentError}
                  onPreview={() => setShowPreview(true)}
                />
              </div>
            </TooltipTrigger>
            {isReadOnly && (
              <TooltipContent>
                <p>Can't edit while campaign is live</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>

      <GmailPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        subject={previewSubject}
        body={previewBody}
        fromEmail={senderEmail}
        fromName={senderName}
        toEmail={recipientEmail}
        toName={recipientName}
      />
    </div>
  );
}

